import { useState } from 'react'
import {
	DndContext,
	KeyboardSensor,
	PointerSensor,
	closestCenter,
	useDroppable,
	useSensor,
	useSensors,
	type CollisionDetection,
	type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ChevronDown, ChevronRight, Copy, GripVertical, LayoutTemplate, MoreHorizontal, Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react'

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from 'components/ui/dropdown-menu'
import { cn } from 'lib/utils'

import { BlockRenderModeProvider } from '../blocks/renderMode'
import type { AssetName } from '../hooks/useAssetNames'
import type { BlockView } from '../hooks/useResolvedBlocks'
import type { DeckDefinition, EntityRef, Slide, Story } from '../types/presentation.types'
import { AnchorChip } from './AnchorChip'
import { SlideRenderer } from './SlideRenderer'
import { SlideStage } from './SlideStage'

/**
 * The left pane: stories as collapsible groups of slide thumbnails (MYD-495).
 *
 * ## Drag and drop
 *
 * One DndContext holds everything. Stories are sortable among themselves;
 * slides are sortable within a story and across stories. Each story also has a
 * drop zone after its last slide, which is how a slide reaches an EMPTY story
 * (there is no slide to drop onto).
 *
 * Collision detection is filtered by what is being dragged: a slide only ever
 * targets slides and story drop zones, a story only other stories. Without
 * that, a slide dragged over a story header would be read as a story move.
 *
 * Every drag has a menu equivalent (Move up / Move down), following
 * ColumnPickerSheet: dnd-kit's keyboard dragging exists but is invisible, and
 * a precise drag is hard on a touch screen.
 *
 * Thumbnails are `SlideRenderer` at 1/7 scale, so they are true miniatures.
 */

const SLIDE = 'slide:'
const STORY = 'story:'
const DROP = 'drop:'
const THUMB_WIDTH = 184

export type PlannedMove =
	| { kind: 'story'; storyId: string; toIndex: number }
	| { kind: 'slide'; slideId: string; toStoryId: string; toIndex: number }

/**
 * What a drop means, as a pure function of the deck and the two dnd-kit ids.
 * Kept out of the component so the rules can be tested without simulating a
 * pointer drag.
 *
 *   story onto story        -> move the story to that story's index
 *   slide onto slide        -> move to that slide's position, in its story
 *   slide onto a drop zone  -> move to the end of that story
 *
 * Returns null for a drop that means nothing (onto itself, onto something
 * that has since disappeared).
 */
export const planDrop = (deck: DeckDefinition, activeId: string, overId: string): PlannedMove | null => {
	if (activeId === overId) return null
	const storyOfSlide = (slideId: string) => deck.stories.find(story => story.slides.some(slide => slide.id === slideId))

	if (activeId.startsWith(STORY)) {
		const toIndex = deck.stories.findIndex(story => `${STORY}${story.id}` === overId)
		return toIndex >= 0 ? { kind: 'story', storyId: activeId.slice(STORY.length), toIndex } : null
	}

	if (!activeId.startsWith(SLIDE)) return null
	const slideId = activeId.slice(SLIDE.length)
	const from = storyOfSlide(slideId)
	if (!from) return null

	if (overId.startsWith(DROP)) {
		const story = deck.stories.find(candidate => candidate.id === overId.slice(DROP.length))
		if (!story) return null
		// Same story: "after the last slide" is the last index once this one is lifted out.
		return { kind: 'slide', slideId, toStoryId: story.id, toIndex: story.id === from.id ? story.slides.length - 1 : story.slides.length }
	}

	if (!overId.startsWith(SLIDE)) return null
	const overSlideId = overId.slice(SLIDE.length)
	const to = storyOfSlide(overSlideId)
	if (!to) return null
	return { kind: 'slide', slideId, toStoryId: to.id, toIndex: to.slides.findIndex(slide => slide.id === overSlideId) }
}

interface SlideSorterProps {
	deck: DeckDefinition
	blocks: Record<string, BlockView | undefined>
	selectedStoryId: string | null
	selectedSlideId: string | null
	assetName: (ref: EntityRef | null) => AssetName | null
	onSelectSlide: (slideId: string) => void
	onSelectStory: (storyId: string) => void
	onAddSlide: (storyId: string) => void
	onAddStory: () => void
	onMoveSlide: (slideId: string, toStoryId: string, toIndex: number) => void
	onMoveStory: (storyId: string, toIndex: number) => void
	onDuplicateSlide: (slideId: string) => void
	onDeleteSlide: (slideId: string) => void
	onDeleteStory: (storyId: string) => void
	/** "Save story as template" (MYD-505). */
	onSaveStoryAsTemplate?: (storyId: string) => void
}

export function SlideSorter(props: SlideSorterProps) {
	const { deck, onMoveSlide, onMoveStory } = props
	const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

	const sensors = useSensors(
		// A small distance, so a click to select a slide is not read as a drag.
		useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	)

	const collisionDetection: CollisionDetection = args => {
		const dragging = String(args.active.id)
		const accepts = dragging.startsWith(STORY)
			? (id: string) => id.startsWith(STORY)
			: (id: string) => id.startsWith(SLIDE) || id.startsWith(DROP)
		return closestCenter({ ...args, droppableContainers: args.droppableContainers.filter(container => accepts(String(container.id))) })
	}

	const handleDragEnd = ({ active, over }: DragEndEvent) => {
		if (!over) return
		const move = planDrop(deck, String(active.id), String(over.id))
		if (move?.kind === 'story') onMoveStory(move.storyId, move.toIndex)
		if (move?.kind === 'slide') onMoveSlide(move.slideId, move.toStoryId, move.toIndex)
	}

	const toggle = (storyId: string) =>
		setCollapsed(previous => {
			const next = new Set(previous)
			if (next.has(storyId)) next.delete(storyId)
			else next.add(storyId)
			return next
		})

	return (
		<div className="flex flex-col gap-3 p-3">
			<DndContext sensors={sensors} collisionDetection={collisionDetection} onDragEnd={handleDragEnd}>
				<SortableContext items={deck.stories.map(story => `${STORY}${story.id}`)} strategy={verticalListSortingStrategy}>
					{deck.stories.map((story, storyIndex) => (
						<StoryGroup
							key={story.id}
							story={story}
							storyIndex={storyIndex}
							storyCount={deck.stories.length}
							collapsed={collapsed.has(story.id)}
							onToggle={() => toggle(story.id)}
							sorter={props}
						/>
					))}
				</SortableContext>
			</DndContext>

			{deck.stories.length === 0 && (
				<p className="px-1 text-xs text-muted-foreground">A story is a run of slides about one asset. Add one to start.</p>
			)}

			<button
				type="button"
				onClick={props.onAddStory}
				className="flex items-center justify-center gap-1.5 rounded-md border border-dashed border-gray-300 py-2 text-xs font-medium text-gray-600 transition-colors hover:border-primary hover:text-primary"
			>
				<Plus className="h-3.5 w-3.5" />
				Add story
			</button>
		</div>
	)
}

function StoryGroup({
	story,
	storyIndex,
	storyCount,
	collapsed,
	onToggle,
	sorter,
}: {
	story: Story
	storyIndex: number
	storyCount: number
	collapsed: boolean
	onToggle: () => void
	sorter: SlideSorterProps
}) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `${STORY}${story.id}` })
	const { setNodeRef: setDropRef, isOver } = useDroppable({ id: `${DROP}${story.id}` })
	const selected = sorter.selectedStoryId === story.id && !sorter.selectedSlideId

	return (
		<section
			ref={setNodeRef}
			style={{ transform: CSS.Transform.toString(transform), transition }}
			className={cn('rounded-lg border bg-white', isDragging ? 'border-primary opacity-60 shadow-md' : 'border-gray-200')}
			aria-label={`Story ${storyIndex + 1}: ${story.title || 'Untitled story'}`}
		>
			<div className={cn('flex items-start gap-1 rounded-t-lg px-1.5 py-1.5', selected && 'bg-primary/5')}>
				<button type="button" className="mt-0.5 cursor-grab rounded p-0.5 text-gray-400 hover:text-gray-600" aria-label="Drag to reorder story" {...attributes} {...listeners}>
					<GripVertical className="h-3.5 w-3.5" />
				</button>
				<button type="button" onClick={onToggle} className="mt-0.5 rounded p-0.5 text-gray-400 hover:text-gray-600" aria-label={collapsed ? 'Expand story' : 'Collapse story'}>
					{collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
				</button>
				<button type="button" onClick={() => sorter.onSelectStory(story.id)} className="min-w-0 flex-1 text-left">
					<span className="block truncate text-xs font-semibold text-gray-900">{story.title || 'Untitled story'}</span>
					<AnchorChip anchor={story.anchor} name={sorter.assetName(story.anchor)} className="mt-0.5" />
				</button>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<button type="button" className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label={`Story actions for ${story.title || 'Untitled story'}`}>
							<MoreHorizontal className="h-4 w-4" />
						</button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem onClick={() => sorter.onAddSlide(story.id)}>
							<Plus className="mr-2 h-4 w-4" /> Add slide
						</DropdownMenuItem>
						<DropdownMenuItem disabled={storyIndex === 0} onClick={() => sorter.onMoveStory(story.id, storyIndex - 1)}>
							<ArrowUp className="mr-2 h-4 w-4" /> Move story up
						</DropdownMenuItem>
						<DropdownMenuItem disabled={storyIndex === storyCount - 1} onClick={() => sorter.onMoveStory(story.id, storyIndex + 1)}>
							<ArrowDown className="mr-2 h-4 w-4" /> Move story down
						</DropdownMenuItem>
						{sorter.onSaveStoryAsTemplate && (
							<DropdownMenuItem onClick={() => sorter.onSaveStoryAsTemplate?.(story.id)}>
								<LayoutTemplate className="mr-2 h-4 w-4" /> Save story as template
							</DropdownMenuItem>
						)}
						<DropdownMenuSeparator />
						<DropdownMenuItem onClick={() => sorter.onDeleteStory(story.id)} className="text-destructive focus:text-destructive">
							<Trash2 className="mr-2 h-4 w-4" /> Delete story
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>

			{!collapsed && (
				<div className="space-y-2 px-2 pb-2">
					<SortableContext items={story.slides.map(slide => `${SLIDE}${slide.id}`)} strategy={verticalListSortingStrategy}>
						{story.slides.map((slide, slideIndex) => (
							<SlideThumb key={slide.id} slide={slide} story={story} slideIndex={slideIndex} sorter={sorter} />
						))}
					</SortableContext>
					<div
						ref={setDropRef}
						className={cn('rounded-md transition-colors', isOver ? 'h-8 border-2 border-dashed border-primary bg-primary/5' : 'h-1')}
					/>
					<button
						type="button"
						onClick={() => sorter.onAddSlide(story.id)}
						className="flex w-full items-center justify-center gap-1 rounded-md py-1 text-[11px] font-medium text-gray-500 hover:bg-gray-50 hover:text-primary"
					>
						<Plus className="h-3 w-3" />
						Add slide
					</button>
				</div>
			)}
		</section>
	)
}

function SlideThumb({ slide, story, slideIndex, sorter }: { slide: Slide; story: Story; slideIndex: number; sorter: SlideSorterProps }) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `${SLIDE}${slide.id}` })
	const selected = sorter.selectedSlideId === slide.id
	const last = story.slides.length - 1

	return (
		<div
			ref={setNodeRef}
			style={{ transform: CSS.Transform.toString(transform), transition }}
			className={cn('group relative rounded-md', isDragging && 'z-10 opacity-60')}
		>
			<button
				type="button"
				onClick={() => sorter.onSelectSlide(slide.id)}
				className={cn(
					'block w-full rounded-md border-2 bg-white p-0.5 text-left transition-colors',
					selected ? 'border-primary' : 'border-transparent hover:border-gray-300',
				)}
				aria-label={`Slide ${slideIndex + 1}: ${slide.title || 'Untitled'}`}
				aria-current={selected ? 'true' : undefined}
				{...attributes}
				{...listeners}
			>
				<SlideStage width={THUMB_WIDTH} className="pointer-events-none overflow-hidden rounded-sm border border-gray-100">
					{/* A live map per thumbnail would exhaust the page's WebGL contexts. */}
					<BlockRenderModeProvider value="thumbnail">
						<SlideRenderer slide={slide} blocks={sorter.blocks} />
					</BlockRenderModeProvider>
				</SlideStage>
				<span className="mt-0.5 flex items-center gap-1 px-0.5 text-[10px] text-gray-500">
					<span className="font-semibold text-gray-400">{slideIndex + 1}</span>
					<span className="truncate">{slide.title || 'Untitled'}</span>
				</span>
			</button>

			<div className="absolute right-1 top-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<button type="button" className="rounded bg-white/90 p-0.5 text-gray-500 shadow-sm hover:text-gray-900" aria-label={`Slide actions for slide ${slideIndex + 1}`}>
							<MoreHorizontal className="h-3.5 w-3.5" />
						</button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem onClick={() => sorter.onDuplicateSlide(slide.id)}>
							<Copy className="mr-2 h-4 w-4" /> Duplicate
						</DropdownMenuItem>
						<DropdownMenuItem disabled={slideIndex === 0} onClick={() => sorter.onMoveSlide(slide.id, story.id, slideIndex - 1)}>
							<ArrowUp className="mr-2 h-4 w-4" /> Move up
						</DropdownMenuItem>
						<DropdownMenuItem disabled={slideIndex === last} onClick={() => sorter.onMoveSlide(slide.id, story.id, slideIndex + 1)}>
							<ArrowDown className="mr-2 h-4 w-4" /> Move down
						</DropdownMenuItem>
						<DropdownMenuSeparator />
						<DropdownMenuItem onClick={() => sorter.onDeleteSlide(slide.id)} className="text-destructive focus:text-destructive">
							<Trash2 className="mr-2 h-4 w-4" /> Delete
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</div>
	)
}

export default SlideSorter
